import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";

function waitForWindowLoad() {
  if (document.readyState === "complete") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    window.addEventListener("load", resolve, { once: true });
  });
}

function waitForFonts() {
  if (!document.fonts || !document.fonts.ready) {
    return Promise.resolve();
  }
  return Promise.race([
    document.fonts.ready,
    new Promise((resolve) => setTimeout(resolve, 5000)),
  ]);
}

/** Resolve after the next two frames so layout, particles, and first GSAP ticks can run. */
function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

export default function LoadingScreen({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [exiting, setExiting] = useState(false);
  const reduceMotion = useMediaQuery({
    query: "(prefers-reduced-motion: reduce)",
  });
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    let cancelled = false;
    const start = performance.now();
    const minVisibleMs = reduceMotion ? 200 : 950;
    const easeToward92 = () => {
      const elapsed = performance.now() - start;
      const target = 92 * (1 - Math.exp(-elapsed / 2200));
      setProgress((prev) => Math.max(prev, Math.min(target, 92)));
    };
    const timer = setInterval(easeToward92, 48);

    const finish = async () => {
      clearInterval(timer);
      if (cancelled) return;
      setFinishing(true);
      requestAnimationFrame(() => {
        if (!cancelled) setProgress(100);
      });
      await new Promise((r) =>
        setTimeout(r, reduceMotion ? 60 : 380)
      );
      if (cancelled) return;
      setExiting(true);
      await new Promise((r) =>
        setTimeout(r, reduceMotion ? 120 : 400)
      );
      if (cancelled) return;
      onDoneRef.current();
    };

    const boot = async () => {
      await Promise.all([waitForFonts(), waitForWindowLoad()]);
      await waitForPaint();
      const elapsed = performance.now() - start;
      const remaining = Math.max(0, minVisibleMs - elapsed);
      await new Promise((r) => setTimeout(r, remaining));
      if (cancelled) return;
      await finish();
    };

    boot();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [reduceMotion]);

  const rounded = Math.round(progress);

  return (
    <div
      className={`boot-loader${exiting ? " boot-loader--exit" : ""}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={rounded}
      aria-label="Loading portfolio"
    >
      <div className="boot-loader__inner">
        <div className="boot-loader__track">
          <div
            className={
              finishing
                ? "boot-loader__fill boot-loader__fill--finishing"
                : "boot-loader__fill"
            }
            style={{ transform: `scaleX(${progress / 100})` }}
          />
        </div>
        <p className="boot-loader__label">
          <span className="boot-loader__glitch" data-text="Loading">
            Loading
          </span>
        </p>
      </div>
    </div>
  );
}
